import { PropsWithChildren } from 'react';
import { WrappedFieldArrayProps } from 'redux-form';
import { omit } from 'lodash';

import * as fileUtil from '@common/utils/file';
import createTempId from '@common/utils/create-temp-id';

import { Attachment, FileInProgress, FileInputValue } from '@common/types/objects';
import { ErrorType } from '../../message';

// list of image mime types that are deemed important by this article
// https://developer.mozilla.org/en-US/docs/Web/HTTP/Basics_of_HTTP/MIME_types/Common_types
// minus 'webp', 'svg' and 'vnd.microsoft.icon' formats
export const IMAGES_ACCEPT = [
  'avif', 'bmp', 'gif', 'jpeg', 'jpg', 'png', 'tiff',
].map((format: string) => `image/${format}`).join(', ');

export const VIDEO_ACCEPT = 'video/*';

export const ALL_ACCEPT = [
  'application/pdf',
  '.mp3', '.wav', '.aac',
  '.doc', '.docx', '.docm', '.dotx', '.dotm', '.pages', '.txt',
  '.csv', '.xls', '.xlsx', '.xlsm', '.xltm', '.numbers',
  '.ppt', '.pptx', '.pps', '.ppsx', '.pptm', '.potx', '.potm', '.key', '.odp',
].join(',');


export type FileInputOwnProps = PropsWithChildren<{
  unsplash?: boolean;
  accept?: string;
  acceptedExtLabel?: string;
  className?: string;
  placeholder?: string;
  maxFileSize?: number;
  disabled?: boolean;
  processFile?: boolean;
  onChange: (value?: FileInputValue, index?: number, originalFile?: FileInputValue) => void;
  onError?: (rejectedFiles: ErrorType[]) => void;
  multiple?: boolean | number;
  value?: FileInputValue | FileInputValue[] | null;
}>;

export type FileInputProps = Omit<FileInputOwnProps, 'value' | 'onChange'>;

export type FileInputHookFormProps = FileInputProps & {
  fields: FileInputValue[];
  /** When set (e.g. from a ref), used for id lookups so async uploads see current rows, not a stale closure. */
  getFields?: () => FileInputValue[];
  insert: (index:number, data?: FileInputValue) => void;
  update: (index: number, data?: FileInputValue) => void;
  remove: (index: number) => void;
};

export const getFileInputHookFormProps = ({
  fields, getFields, multiple, insert, update, remove, ...otherProps
}: FileInputHookFormProps) => ({
  ...otherProps,
  value: fields,
  multiple,
  onChange: (file?: FileInputValue, index?: number, originalFile?: FileInputValue) => {
    const currentFields = getFields?.() ?? fields;
    if (multiple && multiple !== 1) {
      if (file !== undefined || originalFile !== undefined) {
        const idForLookup = originalFile?.id ?? file?.id;
        const existingIndex = idForLookup !== undefined
          ? currentFields.findIndex((f) => f.id === idForLookup)
          : -1;
        if (existingIndex !== -1) {
          if (file) {
            update(existingIndex, file);
          } else {
            remove(existingIndex);
          }
        } else if (file) {
          if (typeof index === 'number') {
            if (originalFile !== undefined) {
              update(index, file);
            } else {
              insert(index, file);
            }
          } else {
            insert(currentFields.length, file);
          }
        } else if (originalFile !== undefined && typeof index === 'number') {
          remove(index);
        }
      }
    } else if (file) {
      update(0, file);
    } else {
      remove(0);
    }
  },
});

export type FileInpuFormProps = WrappedFieldArrayProps & FileInputProps;

export const getMultipleFileInputFormProps = ({
  fields, multiple, ...otherProps
}: FileInpuFormProps) => ({
  ...omit(otherProps, ['meta', 'fields']),
  multiple,
  value: fields.getAll(),
  onChange: (file?: FileInputValue, index?: number, originalFile?: FileInputValue) => {
    const idForLookup = originalFile?.id ?? file?.id;
    const allFields = fields.getAll() || [];
    if (file !== undefined || originalFile !== undefined) {
      const currentIndex = idForLookup !== undefined
        ? allFields.findIndex((f: any) => f.id === idForLookup)
        : -1;
      if (currentIndex !== -1) {
        if (file) fields.splice(currentIndex, 1, file);
        else fields.remove(currentIndex);
      } else if (file) {
        if (typeof index === 'number') {
          if (originalFile !== undefined) {
            fields.splice(index, 1, file);
          } else {
            fields.splice(index, 0, file);
          }
        } else {
          fields.push(file);
        }
      } else if (originalFile !== undefined && typeof index === 'number') {
        fields.remove(index);
      }
    }
  },
});

export enum FileErrorType {
  INVALID = 'invalid',
  MAX_FILE_SIZE = 'maxFileSize',
  MAX_AMOUNT_OF_FILES = 'maxAmountOfFiles',
}

export const RejectedFileTypes = {
  [FileErrorType.MAX_AMOUNT_OF_FILES]: 'common:too_many_files',
  [FileErrorType.MAX_FILE_SIZE]: 'common:file_is_too_large',
  [FileErrorType.INVALID]: 'common:file_is_invalid',
};

type Options = {
  maxFileSize?: number;
  processFile?: boolean;
  multiple?: number | boolean;
};

type SetFormFileAttachmentsParams = {
  files: File[];
  value?: FileInputValue | FileInputValue[] | null;
  organisationId?: string;
  onChange?: ((value?: FileInputValue, index?: number, originalFile?: FileInputValue) => void);
  onError?: (errors: ErrorType[]) => void;
  options: Options;
};

export const createFileInProgress = (file: File): FileInProgress => ({
  id: createTempId(),
  file_name: file.name,
  file_type: file.type,
  processing: false,
  preview: null,
  file,
});

type PendingUpload = {
  file: FileInProgress;
  fileIndex: number | undefined;
  promise: Promise<Attachment>;
  cancelState: { cancelled: boolean };
};

export const setFormFileAttachments = ({
  files,
  value,
  organisationId,
  onChange,
  onError,
  options: { maxFileSize = 30, processFile, multiple },
}: SetFormFileAttachmentsParams) => {
  if (files.length === 0) return;

  const [filesToUpload, excessFiles] = typeof multiple === 'number' && Array.isArray(value)
    ? [files.slice(0, multiple - (value?.length || 0)), files.slice(multiple - (value?.length || 0))]
    : [files];

  if (onError && excessFiles && excessFiles.length > 0) {
    onError([{
      translationKey: RejectedFileTypes[FileErrorType.MAX_AMOUNT_OF_FILES],
      props: { amount: excessFiles.length, maxAmountOfFiles: `${multiple}` },
    }]);
    return;
  }

  void (async () => {
    const rejectedFiles: ErrorType[] = [];
    const pending: PendingUpload[] = [];

    for (let index = 0; index < filesToUpload.length; index++) {
      const f = filesToUpload[index];
      const file = createFileInProgress(f);

      if (maxFileSize && !fileUtil.isValidFile(file.file, maxFileSize)) {
        rejectedFiles.push({
          translationKey: RejectedFileTypes[FileErrorType.MAX_FILE_SIZE],
          props: { fileName: file.file.name, maxFileSize },
        });
        continue;
      }

      const fileIndex = (Array.isArray(value) || typeof multiple === 'number')
        ? (Array.isArray(value) ? value.length : 0) + index - rejectedFiles.length
        : undefined;

      // Sequential: preview + start upload per file so onChange(processing) runs in index order (no-await-in-loop).
      // eslint-disable-next-line no-await-in-loop
      file.preview = await fileUtil.getImageBase64(f);

      if (processFile) {
        file.processing = true;

        const payload = new FormData();
        payload.append('file', f);

        // eslint-disable-next-line no-await-in-loop
        const { onProgress, promise } = await fileUtil.upload(
          `/v2/organisations/${organisationId}/files`,
          payload,
        );

        file.progress = onProgress;
        file.promise = promise;
        const cancelState = { cancelled: false };
        file.cancelPromise = () => {
          cancelState.cancelled = true;
        };

        pending.push({ file, fileIndex, promise, cancelState });
        if (onChange) onChange(file, fileIndex);
      } else if (onChange) {
        onChange(file);
      }
    }

    if (onError && rejectedFiles.length > 0) onError(rejectedFiles);

    await Promise.all(pending.map(async ({ file, fileIndex, promise, cancelState }) => {
      try {
        const attachment: Attachment = await promise;
        if (cancelState.cancelled) {
          if (onChange) onChange(undefined, fileIndex, file);
          return;
        }
        attachment.preview = file.preview;
        if (onChange) onChange(attachment, fileIndex, file);
      } catch (error: any) {
        if (onChange) onChange(undefined, fileIndex, file);
        if (cancelState.cancelled) return;
        if (onError) onError([{
          translationKey: RejectedFileTypes[FileErrorType.INVALID],
          props: {
            fileName: file.file.name,
            error: error?.status_code !== undefined
              ? `${error.status_code} - ${error.type}: ${error.detail}`
              : String(error),
          },
        }]);
      }
    }));
  })();
};

